/**
 * Centralised role-membership helpers for SAM Rumah front-end.
 *
 * The single source of truth for "what counts as admin / back-office / customer-facing".
 * Add a new role here once, propagate to the whole app automatically.
 *
 * History: created May 2026 after a white-screen redirect-loop incident triggered by
 * the RBAC migration (admin → super_admin). The bug was caused by hard-coded
 * `role === 'admin'` checks scattered across 10 files. Never let that happen again —
 * always use the helpers in this module.
 */

// ----------------------------- ROLE SETS -----------------------------

/** Roles that mean "administrator" of some flavour (full admin permissions surface). */
export const ADMIN_LIKE_ROLES = Object.freeze(new Set([
  'super_admin',
  'operation_admin',
  'admin', // legacy — kept for any non-migrated user rows
]));

/** Roles that belong to the back-office side of the product (admin portal access). */
export const BACKOFFICE_ROLES = Object.freeze(new Set([
  ...ADMIN_LIKE_ROLES,
  'pic',
  'account',
  // legacy synonyms kept for safety
  'accountant',
  'finance',
]));

/** Roles that use the customer/agent side of the product. */
export const CUSTOMER_FACING_ROLES = Object.freeze(new Set([
  'customer',
  'agent',
]));

// ----------------------------- PREDICATES -----------------------------

export const isAdminLikeRole   = (role) => ADMIN_LIKE_ROLES.has(role);
export const isSuperAdminRole  = (role) => role === 'super_admin';
export const isOpsAdminRole    = (role) => role === 'operation_admin';
export const isBackofficeRole  = (role) => BACKOFFICE_ROLES.has(role);
export const isCustomerFacingRole = (role) => CUSTOMER_FACING_ROLES.has(role);

// ----------------------------- ROUTING -----------------------------

/** Default landing path for a role (used by post-login redirect + ProtectedRoute bounce). */
export const roleHomePath = (role) => {
  if (isBackofficeRole(role)) return '/admin';
  if (isCustomerFacingRole(role)) return '/dashboard';
  return '/';
};

/** Array form for React-Router `allowedRoles` props (back-office route group). */
export const BACKOFFICE_ROLES_ARRAY = Array.from(BACKOFFICE_ROLES);

/** Array form for React-Router `allowedRoles` props (customer/agent route group). */
export const CUSTOMER_FACING_ROLES_ARRAY = Array.from(CUSTOMER_FACING_ROLES);
